import { useEffect, useMemo, useState } from "react";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";

const t = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const n = (v, dp = 1) => (v == null ? "—" : Number(v).toFixed(dp));
const COLORS = ["#f43f5e", "#38bdf8", "#34d399", "#fbbf24", "#a78bfa", "#fb923c"];
const who = (p) => `${p.user}${p.role ? ` (${p.role})` : ""} ${p.dir === "send" ? "→ mic out" : `← hears ${p.remote || "?"}`}`;

function Sparkline({ samples, keys, field, max, label }) {
  const W = 640, H = 80;
  if (!samples.length) return null;
  const t0 = new Date(samples[0].ts).getTime(), t1 = Math.max(t0 + 1, new Date(samples[samples.length - 1].ts).getTime());
  const x = (iso) => ((new Date(iso).getTime() - t0) / (t1 - t0)) * W;
  const y = (v) => H - Math.min(1, (Number(v) || 0) / max) * H;
  return (
    <div data-testid={`audio-spark-${field}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label} · scale 0–{max}</div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-20 bg-black/40 border border-white/10">
        {keys.map((k, i) => {
          const pts = samples.filter((s) => s.key === k && s[field] != null).map((s) => `${x(s.ts).toFixed(1)},${y(s[field]).toFixed(1)}`);
          return pts.length > 1 ? <polyline key={k} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="1.5" points={pts.join(" ")} /> : null;
        })}
      </svg>
    </div>
  );
}

// Admin → Live Diagnostics: per-stream WebRTC audio stats (loss / jitter / RTT / concealment) from every participant.
export default function AudioDiagnosticsPanel() {
  const [streams, setStreams] = useState(null);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/telemetry/audio/streams").then(({ data }) => alive && setStreams(data)).catch(() => alive && setStreams({ enabled: false, streams: [] }));
    load();
    const i = setInterval(load, 15000);
    return () => { alive = false; clearInterval(i); };
  }, []);

  useEffect(() => {
    if (!sel) { setDetail(null); return undefined; }
    let alive = true;
    const load = () => api.get(`/telemetry/audio/stream/${sel}`).then(({ data }) => alive && setDetail(data)).catch(() => {});
    load();
    const i = setInterval(load, 10000);
    return () => { alive = false; clearInterval(i); };
  }, [sel]);

  const keys = useMemo(() => (detail?.participants || []).slice(0, 6).map((p) => p.key), [detail]);

  if (!streams) return null;
  if (!streams.enabled) return <div className="text-sm text-zinc-500" data-testid="audio-diag-disabled">Audio telemetry is off (LIVE_AUDIO_TELEMETRY_ENABLED).</div>;

  return (
    <div className="space-y-3" data-testid="audio-diagnostics">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400"><Activity size={13} /> Audio diagnostics · last 7 days · sampled every 5 s from each participant&apos;s browser</div>
      {streams.streams.length === 0 && <div className="text-sm text-zinc-500" data-testid="audio-diag-empty">No audio samples yet. They appear once someone is in a Live room.</div>}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {streams.streams.map((s) => (
          <button key={s.stream_id} type="button" onClick={() => setSel(s.stream_id)} data-testid={`audio-stream-${s.stream_id}`}
            className={`shrink-0 text-left border p-2 text-xs transition-colors ${sel === s.stream_id ? "border-rose-500 bg-rose-500/10" : "border-white/10 hover:border-white/30"}`}>
            <div className="text-white">@{s.host || "?"} · {s.title || "untitled"} {s.is_live && <span className="text-rose-400">● LIVE</span>}</div>
            <div className="text-zinc-400">{s.participants} participants · {s.samples} samples · max loss <b className={s.max_loss >= 5 ? "text-rose-300" : "text-zinc-200"}>{n(s.max_loss)}%</b> · {s.spikes} spikes{s.sustained ? <> · <b className="text-rose-300">{s.sustained} sustained</b></> : ""} · audible gaps <b className={s.audible_concealed_ms >= 1000 ? "text-rose-300" : "text-zinc-200"}>{s.audible_concealed_ms ?? 0} ms</b> <span className="text-zinc-500">({s.silent_concealed_ms ?? 0} ms silent · {s.concealment_events} events)</span></div>
            <div className="text-zinc-500">{t(s.first_ts)} → {t(s.last_ts)}</div>
          </button>
        ))}
      </div>

      {detail && detail.enabled && (
        <div className="space-y-3" data-testid="audio-stream-detail">
          <div className="grid lg:grid-cols-2 gap-3">
            <Sparkline samples={detail.samples} keys={keys} field="loss_pct" max={20} label="Packet loss %" />
            <Sparkline samples={detail.samples} keys={keys} field="jitter_ms" max={100} label="Jitter ms" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audio-participants">
              <thead className="text-zinc-500 uppercase tracking-widest text-[10px]"><tr>{["Participant", "Loss avg / max", "Loss true / 30 s max", "Jitter avg / max", "RTT avg / max", "Audible gaps", "Worst", "Platform / net", "Window"].map((h) => <th key={h} className="text-left py-1 pr-3 font-normal">{h}</th>)}</tr></thead>
              <tbody>
                {detail.participants.map((p, i) => (
                  <tr key={p.key} className="border-t border-white/5" data-testid="audio-participant-row">
                    <td className="py-1.5 pr-3 text-white"><span className="inline-block w-2 h-2 mr-1.5" style={{ background: COLORS[i % COLORS.length] }} />{who(p)}</td>
                    <td className={`pr-3 ${p.loss_max >= 5 ? "text-rose-300" : "text-zinc-300"}`}>{n(p.loss_avg, 2)}% / {n(p.loss_max)}%</td>
                    <td className={`pr-3 ${p.loss_30s_max >= 5 ? "text-rose-300" : "text-zinc-300"}`} title="true = lost ÷ (received+lost) over the whole session · 30 s max = worst rolling 30 s">{p.loss_true_pct == null ? "—" : `${n(p.loss_true_pct, 2)}%`} / {n(p.loss_30s_max)}%</td>
                    <td className={`pr-3 ${p.jitter_max >= 80 ? "text-amber-300" : "text-zinc-300"}`}>{n(p.jitter_avg)} / {n(p.jitter_max)}</td>
                    <td className="pr-3 text-zinc-300">{p.rtt_avg == null ? "—" : `${n(p.rtt_avg, 0)} / ${n(p.rtt_max, 0)}`}</td>
                    <td className={`pr-3 ${p.audible_concealed_ms >= 1000 ? "text-rose-300" : "text-zinc-300"}`} title="audible concealed audio (ms) · silent/DTX concealment in grey · raw concealment events">{p.audible_concealed_ms ?? 0} ms <span className="text-zinc-500">({p.silent_concealed_ms ?? 0} silent · {p.concealment_events} ev)</span></td>
                    <td className={`pr-3 ${p.worst_quality ? "text-rose-300" : "text-emerald-300"}`}>{p.worst_quality || "ok"}</td>
                    <td className="pr-3 text-zinc-400">{p.platform || "?"} / {p.net_type || "?"}</td>
                    <td className="text-zinc-500">{t(p.first_ts)}–{t(p.last_ts)} · {p.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {detail.participants.some((p) => p.dir === "send") && (
            <div className="overflow-x-auto" data-testid="audio-capture-table">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Mic capture & encoding (send side) — compare each guest against the host</div>
              <table className="w-full text-xs">
                <thead className="text-zinc-500 uppercase tracking-widest text-[10px]"><tr>{["Participant", "Role", "Codec", "Bitrate", "Opus params", "Capture", "EC / NS / AGC", "Device", "Mic level"].map((h) => <th key={h} className="text-left py-1 pr-3 font-normal">{h}</th>)}</tr></thead>
                <tbody>
                  {detail.participants.filter((p) => p.dir === "send").map((p) => (
                    <tr key={p.key} className="border-t border-white/5" data-testid="audio-capture-row">
                      <td className="py-1.5 pr-3 text-white">@{p.user}</td>
                      <td className="pr-3 text-zinc-300">{p.role || "?"}</td>
                      <td className="pr-3 text-zinc-300">{p.codec || "—"}</td>
                      <td className="pr-3 text-zinc-300">{p.bitrate_kbps == null ? "—" : `${n(p.bitrate_kbps)} kbps`}</td>
                      <td className="pr-3 text-zinc-400 max-w-[220px] truncate" title={p.fmtp || ""}>{p.fmtp || "—"}</td>
                      <td className="pr-3 text-zinc-300">{p.capture ? `${p.capture.sample_rate || "?"} Hz · ${p.capture.channels || "?"}ch` : "—"}</td>
                      <td className="pr-3 text-zinc-300">{p.capture ? ["ec", "ns", "agc"].map((k) => (p.capture[k] == null ? "?" : p.capture[k] ? "on" : "off")).join(" / ") : "—"}</td>
                      <td className="pr-3 text-zinc-400 max-w-[160px] truncate" title={p.capture?.device || ""}>{p.capture?.device || "—"}</td>
                      <td className="text-zinc-300">{p.audio_level_avg == null ? "—" : n(p.audio_level_avg, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {detail.bursts?.length > 0 && (
            <div data-testid="audio-bursts">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Audible gap bursts ({detail.bursts.length}) — ≥200 ms audible concealment per 5 s, clustered within 30 s, with the nearest mic/role event</div>
              <ul className="max-h-40 overflow-y-auto text-xs divide-y divide-white/5">
                {[...detail.bursts].reverse().map((b, i) => (
                  <li key={`${b.start}-${i}`} className="py-1 flex gap-3 text-zinc-300">
                    <span className="text-zinc-500 w-32 shrink-0">{t(b.start)} → {t(b.end)}</span>
                    <span className="text-rose-300 shrink-0">{b.count} spikes</span>
                    <span className="text-white truncate">{b.users.join(", ")}</span>
                    <span className="text-amber-300 shrink-0">{b.near_event ? `near: ${b.near_event}` : "no nearby event"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {detail.events?.length > 0 && (
            <div data-testid="audio-events">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Mic / participant events ({detail.events.length})</div>
              <ul className="max-h-40 overflow-y-auto text-xs divide-y divide-white/5">
                {[...detail.events].reverse().map((e, i) => (
                  <li key={`${e.ts}-${i}`} className="py-1 flex gap-3 text-zinc-300">
                    <span className="text-zinc-500 w-20 shrink-0">{t(e.at || e.ts)}</span>
                    <span className="text-amber-300 shrink-0">{e.kind}</span>
                    <span className="text-white truncate">{e.who ? `@${e.who}` : ""}{e.detail ? ` · ${e.detail}` : ""}</span>
                    <span className="text-zinc-500 shrink-0">seen by @{e.user} ({e.platform || "?"})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Spikes ({detail.spikes.length}) · loss ≥5% in a 5 s window (RTCP-aligned, can over-read; see 30 s) · jitter ≥80 ms · RTT ≥400 ms · ≥200 ms audible gaps in 5 s</div>
            {detail.spikes.length === 0 ? <div className="text-xs text-zinc-500" data-testid="audio-no-spikes">No spikes recorded.</div> : (
              <ul className="max-h-56 overflow-y-auto text-xs divide-y divide-white/5" data-testid="audio-spikes">
                {[...detail.spikes].reverse().map((s, i) => (
                  <li key={`${s.ts}-${i}`} className="py-1 flex gap-3 text-zinc-300">
                    <span className="text-zinc-500 w-20 shrink-0">{t(s.ts)}</span>
                    <span className="text-white truncate">{s.user} {s.dir === "send" ? "→ mic out" : `← ${s.remote || "?"}`}</span>
                    <span className="text-rose-300 shrink-0">{s.reasons.join(", ")}</span>
                    <span className="text-zinc-400 shrink-0">loss {n(s.loss_pct)}% · jit {n(s.jitter_ms, 0)} · rtt {n(s.rtt_ms, 0)} {s.loss_pct_30s != null ? ` · 30s ${n(s.loss_pct_30s)}%` : ""}{s.packets_lost != null ? ` · ${s.packets_lost}/${(s.packets || 0) + (s.packets_lost || 0)} pkts` : ""} · audible {s.audible_concealed_ms ?? 0} ms{s.conn_quality ? ` · ${s.conn_quality}` : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
